import React, { useEffect, useState } from 'react';
import { Loader2, Database, CheckCircle2, AlertTriangle, KeyRound, ServerCog } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../ConfirmDeleteDialog';
import { useConfirm } from '../useConfirm';

import { allocateSqlApi } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

/**
 * SQL connection form for Allocate Live Import.
 * Sits on /assets/import when "EQUIPR Allocate (Live SQL)" source is selected.
 *
 * Props:
 *   onPreview(previewResult) — handed up to the parent so it can swap into
 *                              the diff preview step (shared with CSV flow).
 *   onSourceCommitted(commitResult) — handed up after a successful import.
 */
const AllocateSqlConnectForm = ({ onPreview, onSourceCommitted, forceResync = false }) => {
  const { confirm, dialogProps } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [form, setForm] = useState({
    server: '',
    port: 1433,
    database: '',
    username: '',
    password: '',
    encrypt: true,
    trust_server_certificate: false,
    changed_since: '',
    save: true,
  });
  const [defaults, setDefaults] = useState({ default_server: null, default_database: null });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await allocateSqlApi.getConfig();
        setHasSaved(data.has_saved_credentials);
        setSavedAt(data.saved_at);
        setLastSyncedAt(data.last_synced_at);
        setDefaults({
          default_server: data.default_server,
          default_database: data.default_database,
        });
        setForm((f) => ({
          ...f,
          server: data.server || data.default_server || '',
          port: data.port || 1433,
          database: data.database || data.default_database || '',
          username: data.username || '',
          // Password never returned — left blank. Saved-credentials path uses the encrypted copy.
          password: '',
          encrypt: data.encrypt ?? true,
          trust_server_certificate: data.trust_server_certificate ?? false,
        }));
      } catch (e) {
        // 404 or 500 just means nothing saved — that's fine.
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildBody = () => ({
    server: form.server.trim(),
    port: Number(form.port) || 1433,
    database: form.database.trim(),
    username: form.username.trim(),
    password: form.password,
    encrypt: form.encrypt,
    trust_server_certificate: form.trust_server_certificate,
    changed_since: form.changed_since || null,
    save: form.save,
    // `force_resync` comes from the unified parent toggle (AssetImportPage)
    // so the same "Overwrite/Force re-import" checkbox controls all 3 sources.
    force_resync: forceResync,
  });

  const explainError = (err) => {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    return err.message || 'Something went wrong';
  };

  const handleTest = async () => {
    if (!validate()) return;
    setTesting(true);
    try {
      await allocateSqlApi.testConnection(buildBody());
      toast.success('Connection OK — stored procedure detected');
      if (form.save) setHasSaved(true);
    } catch (err) {
      toast.error(explainError(err));
    } finally {
      setTesting(false);
    }
  };

  const handlePreview = async () => {
    if (!validate()) return;
    setPreviewing(true);
    const body = buildBody();
    try {
      const { data } = hasSaved && !form.password
        ? await allocateSqlApi.previewSaved(form.changed_since || null, forceResync)
        : await allocateSqlApi.preview(body);
      onPreview?.(data, body);
    } catch (err) {
      toast.error(explainError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!validate()) return;
    setImporting(true);
    try {
      const { data } = hasSaved && !form.password
        ? await allocateSqlApi.commitSaved(form.changed_since || null, forceResync)
        : await allocateSqlApi.commit(buildBody());
      onSourceCommitted?.(data);
    } catch (err) {
      toast.error(explainError(err));
    } finally {
      setImporting(false);
    }
  };

  const handleClear = () => {
    confirm({
      title: 'Forget the saved SQL credentials?',
      description: 'The stored EQUIPR Allocate SQL credentials for this tenant will be removed.',
      confirmLabel: 'Forget',
      testId: 'confirm-forget-sql-creds',
      onConfirm: async () => {
        try {
          await allocateSqlApi.clearConfig();
          setHasSaved(false);
          setSavedAt(null);
          setForm((f) => ({ ...f, password: '' }));
          toast.success('Saved credentials cleared');
        } catch (err) {
          toast.error(explainError(err));
          throw err;
        }
      },
    });
  };

  const validate = () => {
    if (!form.server || !form.database || !form.username) {
      toast.error('Server, database, and username are required');
      return false;
    }
    // Password optional only when we already have saved creds and the field is left blank.
    if (!hasSaved && !form.password) {
      toast.error('Password is required');
      return false;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="allocate-sql-form">
      {/* Status strip */}
      {hasSaved && (
        <div className="rounded-md border border-[#FFD108]/30 bg-[#FFD108]/[0.04] px-3 py-2 text-xs flex items-center justify-between">
          <span className="text-muted-foreground">
            Credentials saved {savedAt && <>· {new Date(savedAt).toLocaleString()}</>}
            {lastSyncedAt && <> · last synced {new Date(lastSyncedAt).toLocaleString()}</>}
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={handleClear} data-testid="allocate-sql-forget">
            Forget
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">SQL Server host</Label>
          <Input
            value={form.server}
            placeholder={defaults.default_server || 'sql-allocate.equipr.internal'}
            onChange={(e) => set('server', e.target.value)}
            data-testid="sql-server-input"
            className="mt-1 font-mono"
          />
          {defaults.default_server && !form.server && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Default: <span className="font-mono">{defaults.default_server}</span>
            </p>
          )}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Port</Label>
          <Input
            type="number"
            value={form.port}
            onChange={(e) => set('port', e.target.value)}
            data-testid="sql-port-input"
            className="mt-1 font-mono"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Database</Label>
          <Input
            value={form.database}
            placeholder={defaults.default_database || 'AllocateDb'}
            onChange={(e) => set('database', e.target.value)}
            data-testid="sql-database-input"
            className="mt-1 font-mono"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">SQL username</Label>
          <Input
            value={form.username}
            placeholder="guardian_read"
            onChange={(e) => set('username', e.target.value)}
            data-testid="sql-username-input"
            className="mt-1 font-mono"
            autoComplete="off"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">
            SQL password
            {hasSaved && (
              <span className="ml-2 text-[10px] normal-case tracking-normal text-[#FFD108]">
                · leave blank to reuse saved password
              </span>
            )}
          </Label>
          <Input
            type="password"
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            placeholder={hasSaved ? '••••••••  (using saved)' : ''}
            data-testid="sql-password-input"
            className="mt-1 font-mono"
            autoComplete="new-password"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">
            Delta sync (optional)
          </Label>
          <Input
            type="datetime-local"
            value={form.changed_since}
            onChange={(e) => set('changed_since', e.target.value)}
            className="mt-1 font-mono"
            data-testid="sql-changed-since"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Leave blank for a full export. Otherwise only assets modified at or after this time will sync.
          </p>
        </div>
        <div className="space-y-2 pt-5">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Save credentials</Label>
            <Switch checked={form.save} onCheckedChange={(v) => set('save', v)} data-testid="sql-save-toggle" />
          </div>
        </div>
      </div>

      <div className="rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground flex gap-3">
        <KeyRound className="w-4 h-4 text-[#FFD108] flex-shrink-0 mt-0.5" />
        <div>
          We recommend a <strong>read-only SQL user</strong> with permission only to
          {' '}<code className="font-mono text-[10px]">EXEC dbo.sp_Guardian_ExportAssets</code>.
          Passwords are encrypted at rest and never returned by the API.
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={handleTest}
          disabled={testing || previewing || importing}
          data-testid="sql-test-btn"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ServerCog className="w-4 h-4 mr-2" />}
          {testing ? 'Testing…' : 'Test connection'}
        </Button>
        <Button
          type="button"
          className="btn-volt ml-auto"
          onClick={handlePreview}
          disabled={testing || previewing || importing}
          data-testid="sql-preview-btn"
        >
          {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
          {previewing ? 'Running SP…' : 'Preview import →'}
        </Button>
      </div>
      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

export default AllocateSqlConnectForm;
